/**
 * Utility per date e calendario
 */

import { WEEKDAYS, WEEKDAY_LABELS, WEEKDAY_SHORT } from './types.js';

/** Icona documento PDF per pulsanti esportazione (fill: currentColor; usare con .btn-pdf) */
export const PDF_DOWNLOAD_ICON_HTML = `<span class="btn-pdf-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" focusable="false"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg></span>`;

const ITALIAN_MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/**
 * Raggruppa i giorni (es. da getDaysInWeekRange) per mese di calendario, in ordine cronologico.
 * @param {{ date: string }[]} days
 * @returns {{ ym: string, label: string, count: number }[]}
 */
export function getItalianMonthGroupsForDays(days) {
  if (!days?.length) return [];
  const groups = [];
  for (const day of days) {
    const ym = (day.date || '').slice(0, 7);
    if (ym.length !== 7) continue;
    const mi = parseInt(ym.slice(5, 7), 10);
    const y = parseInt(ym.slice(0, 4), 10);
    const label = `${ITALIAN_MONTH_NAMES[mi - 1] || ym} ${y}`;
    const prev = groups[groups.length - 1];
    if (prev && prev.ym === ym) prev.count += 1;
    else groups.push({ ym, label, count: 1 });
  }
  return groups;
}

/** YYYY-MM-DD nel calendario locale (stessa convenzione di getDaysInMonth / buildSlotsFromCalendar). */
export function formatLocalISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Ritorna base + indice numerico finale (se presente): "SO12" -> { base: "SO", hasIndex: true, index: 12 }. */
export function parseOrdinalSlotName(name) {
  const raw = String(name || '').trim();
  if (!raw) return { base: '', hasIndex: false, index: 0 };
  const m = raw.match(/^(.*?)(\d+)$/);
  if (!m) return { base: raw, hasIndex: false, index: 0 };
  return {
    base: m[1] ?? '',
    hasIndex: true,
    index: Number.parseInt(m[2], 10) || 0,
  };
}

/**
 * Nome slot univoco nella stessa fascia:
 * - se il nome e' libero, lo mantiene;
 * - se termina con numero (es. SO2), prova il primo indice disponibile (SO1, SO4, ...);
 * - se non termina con numero (es. MedDol), usa MedDol2, MedDol3, ...
 */
export function getUniqueSlotName(desiredName, existingNames = []) {
  const desired = String(desiredName || '').trim();
  if (!desired) return desired;
  const key = (s) => String(s || '').trim().toLowerCase();
  const used = new Set((existingNames || []).map(key).filter(Boolean));
  if (!used.has(key(desired))) return desired;
  const parsed = parseOrdinalSlotName(desired);
  const base = (parsed.base || desired).trim();
  let idx = parsed.hasIndex ? 1 : 2;
  let candidate = `${base}${idx}`;
  while (used.has(key(candidate))) {
    idx += 1;
    candidate = `${base}${idx}`;
  }
  return candidate;
}

/**
 * Mappa categoria → "base name" canonico di default (usato quando l'input
 * non corrisponde ad alcun pattern noto). NORA ha due varianti (RMN/Endo);
 * il default è RMN.
 *
 * Sala Operatoria è sempre numerata: la base canonica include l'indice
 * iniziale ("SO1") in modo che `getUniqueSlotName` produca SO1, SO2, ...
 */
const CATEGORY_CANONICAL_BASE = {
  terapia_intensiva: 'TI',
  sala_operatoria: 'SO1',
  nora: 'RMN',
  medicina_dolore: 'MedDol',
  cure_palliative: 'CP',
  formazione: 'Form',
  direzione: 'Direzione',
};

/**
 * Pattern → base canonica.
 * I pattern sono testati contro l'input normalizzato (lowercase, niente
 * spazi/punteggiatura, eventuali cifre finali rimosse). Match per prefisso.
 * Ordine: dai più specifici ai più generici per evitare collisioni.
 */
const NAME_PATTERN_TO_CANONICAL = [
  [/^(meddol|md|medicinadel|medicinadolor|terapiantalg|terantalg|antalgic)/, 'MedDol'],
  [/^(cp|curepall|palliativ|hospice)/, 'CP'],
  [/^(rmn|risonanz|risma|mri)/, 'RMN'],
  [/^(endo|endoscop)/, 'Endo'],
  [/^(repgiorno|reperibilitagiorno|repdiurn|repdiur|repday|repg)/, 'Rep Giorno'],
  [/^(repnotte|reperibilitanotte|repnotturn|repnight|repn)/, 'Rep Notte'],
  [/^(ambanest|ambulatorioanest|ambulatorianest|visitaanest|ambulator|ambulan)/, 'Amb Anest'],
  // Prima di `^so…`: altrimenti "soguardia" collassa erroneamente a SO1.
  [/^(soguardia|guardia)/, 'SO Guardia'],
  [/^(direzion|direttor|direz|dir)/, 'Direzione'],
  [/^(form|corso|lezione)/, 'Form'],
  [/^(so|salaoper|salaop|blocco)/, 'SO1'],
  [/^(ti|terint|terapiaintensiv|terapint|intensiv|rian)/, 'TI'],
];

/** Lower-case + rimozione diacritici + solo alfanumerici + drop cifre finali. */
function normalizeForCanonicalMatch(input) {
  if (!input) return '';
  return String(input)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .replace(/\d+$/, '');
}

/** SO in Medicina del dolore: "SO MedDol", "SO MedDol2", "SOMedDol3" → nome standard con spazio. */
function canonicalSoMedDolNameFromRaw(rawName) {
  const compact = String(rawName || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
  // "SO MedDol" -> someddol; accetta anche "somedol" (una sola d fra "me" e "ol").
  const m = compact.match(/^somedd?ol(\d+)?$/);
  if (!m) return null;
  const suf = m[1] || '';
  if (!suf || suf === '1') return 'SO MedDol';
  return `SO MedDol${suf}`;
}

function canonicalFromInputName(input) {
  const norm = normalizeForCanonicalMatch(input);
  if (!norm) return null;
  for (const [re, base] of NAME_PATTERN_TO_CANONICAL) {
    if (re.test(norm)) return base;
  }
  return null;
}

/**
 * Restituisce la "base canonica" per uno slot, normalizzando varianti utente
 * verso codici standard (es. "Ter Int 2" → "TI", "Risonanza" → "RMN",
 * "SO 1" → "SO1", "Ter Antalgica" → "MedDol", "Palliative" → "CP").
 *
 * Priorità:
 *   1. timeSlot reperibilita_* → "Rep Giorno"/"Rep Notte" (deterministico).
 *   2. keyword match sull'input dell'utente.
 *   3. activityType non-clinico in sala_operatoria → "Amb Anest".
 *   4. default per categoria (TI/SO1/RMN/MedDol/CP/Form/Direzione).
 *   5. fallback: input pulito (trim) o stringa vuota.
 *
 * Combina con `getUniqueSlotName` per ottenere il nome finale unico
 * nella stessa fascia (es. TI → TI2, SO1 → SO2, MedDol → MedDol2).
 */
export function canonicalizeSlotName(category, rawName, timeSlot, activityType) {
  const ts = String(timeSlot || '').toLowerCase();
  if (ts === 'reperibilita_notte') return 'Rep Notte';
  if (ts === 'reperibilita_giorno' || ts === 'reperibilita') return 'Rep Giorno';
  const soMd = canonicalSoMedDolNameFromRaw(rawName);
  if (soMd) return soMd;
  const fromInput = canonicalFromInputName(rawName);
  if (fromInput) return fromInput;
  const at = String(activityType || '').toLowerCase();
  if (category === 'sala_operatoria' && at === 'non_clinico') return 'Amb Anest';
  if (category === 'direzione' || at === 'direzione') return 'Direzione';
  if (category === 'formazione' || at === 'formazione') return 'Form';
  const def = CATEGORY_CANONICAL_BASE[category];
  if (def) return def;
  return String(rawName || '').trim();
}

/** Versione comoda per slot/template object: estrae i campi e canonicalizza. */
export function canonicalizeSlotObject(s) {
  if (!s) return '';
  return canonicalizeSlotName(s.category, s.name, s.timeSlot, s.activityType);
}

/** Come backend `_norm_slot_name_logical`: solo lower + trim. */
function normSlotNameLogical(name) {
  return String(name ?? '').trim().toLowerCase();
}

/**
 * Rapporto di lavoro cessato alla data indicata.
 *
 * `dataFineRapporto` è l'ULTIMO giorno lavorato: il medico resta attivo per
 * tutto quel giorno ed è cessato dal giorno successivo. Serve per elenco,
 * accesso e assegnabilità — mai per le viste storiche (Carichi, turni passati,
 * export), che devono continuare a mostrarlo.
 *
 * @param {object} doctor
 * @param {string} [refDate] - data YYYY-MM-DD; default: oggi (ora locale)
 * @returns {boolean}
 */
export function isDoctorCeased(doctor, refDate) {
  const dfr = String(doctor?.dataFineRapporto ?? doctor?.data_fine_rapporto ?? '').slice(0, 10);
  if (!dfr) return false;
  let ref = String(refDate || '').slice(0, 10);
  if (!ref) {
    // Data locale, non UTC: toISOString() vicino a mezzanotte darebbe il giorno prima.
    const n = new Date();
    ref = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  }
  return ref > dfr;
}

function isReperibilitaBand(timeSlot, activityType) {
  const ts = String(timeSlot || '').trim().toLowerCase();
  const at = String(activityType ?? '').trim().toLowerCase();
  return ts.startsWith('reperibilita') || at === 'reperibilita';
}

function isLikelyAmbAnestRawName(rawName) {
  const n = String(rawName || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!n) return false;
  if (n === 'amb anest' || n === 'amb anestesia') return true;
  if (n.startsWith('amb anest ')) return true;
  if (/^amb\s*anest/.test(n)) return true;
  return n.includes('ambulatorio') && n.includes('anest');
}

/**
 * Chiave logica per merge/dedup (allineata a `_slot_logical_key` / nome grezzo).
 * Escluso templateId / id modifica.
 */
export function slotLogicalKeyString(s) {
  if (!s) return '';
  const d = String(s.date || '').trim();
  const ts = String(s.timeSlot || s.time_slot || '').trim().toLowerCase();
  const cat = String(s.category || '').trim().toLowerCase();
  const uoc = String(s.uocId ?? s.uoc_id ?? '').trim();
  const atRaw = String(s.activityType ?? s.activity_type ?? 'clinico').trim().toLowerCase();
  const atIn = atRaw || 'clinico';
  const rawName = String(s.name ?? '').trim();

  let nameForKey;
  let atForKey;

  if (isReperibilitaBand(ts, atIn)) {
    const canon = canonicalizeSlotName(cat, rawName, ts, atIn).trim();
    nameForKey = normSlotNameLogical(canon);
    atForKey = 'reperibilita';
  } else if (cat === 'sala_operatoria' && isLikelyAmbAnestRawName(rawName)) {
    nameForKey = 'amb anest';
    atForKey = 'non_clinico';
  } else {
    nameForKey = normSlotNameLogical(rawName);
    atForKey = atIn;
  }

  const at = String(atForKey).trim().toLowerCase() || 'clinico';
  if (!d) return '';
  return `${d}\u0000${ts}\u0000${cat}\u0000${uoc}\u0000${at}\u0000${nameForKey}`;
}

function fnv1a32Utf8(str) {
  const buf = new TextEncoder().encode(str);
  let h = 2166136261 >>> 0;
  for (let i = 0; i < buf.length; i++) {
    h ^= buf[i];
    h = Math.imul(h, 16777619) >>> 0;
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Chiave persistita stabile per slot da calendario (template / mod perpetua):
 * `YYYY-MM-DD-lgk-<hex>-<hex>` — allineabile dopo ricreazione template (no templateId in chiave).
 */
export function stableCalendarSlotKey(slot) {
  const lk = slotLogicalKeyString(slot);
  if (!lk) return '';
  const d = String(slot?.date || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return '';
  const h1 = fnv1a32Utf8(lk);
  const h2 = fnv1a32Utf8(`${lk}|lgk|v1`);
  return `${d}-lgk-${h1}-${h2}`;
}

/**
 * Variante per la deduplicazione dei nomi *esistenti*: applica la stessa base
 * canonica, ma **preserva** l'indice trailing dell'input (se presente).
 *
 * Necessaria perché `canonicalizeSlotName` rimuove le cifre finali per
 * normalizzare l'input utente ("TI 2" → "TI"), ma quando si elabora la lista
 * degli slot già presenti dobbiamo distinguere "SO1", "SO2", "SO3" (altrimenti
 * collassano tutti a "SO1" e l'unicità non funziona).
 *
 * Regole:
 *   - input con indice trailing → `${base canonica}${indice input}`
 *     (es. "SO 2" → "SO2", "Ter Int 3" → "TI3", "NORA 1" → "RMN1").
 *   - input senza indice → restituisce direttamente la base canonica
 *     (es. "SO Guardia" → "SO Guardia", "Risonanza" → "RMN", "TI" → "TI").
 */
export function canonicalizeSlotNameKeepIndex(category, name, timeSlot, activityType) {
  const inputParsed = parseOrdinalSlotName(String(name || '').trim());
  const canonical = canonicalizeSlotName(category, name, timeSlot, activityType);
  if (!canonical) return '';
  if (!inputParsed.hasIndex) return canonical;
  const baseParsed = parseOrdinalSlotName(canonical);
  const base = (baseParsed.base || canonical).trim();
  return `${base}${inputParsed.index}`;
}

/**
 * Nome minimo quando `name` è vuoto (allineato a `backend/main.py` → `_default_planner_slot_name`).
 */
export function defaultPlannerSlotName(category, timeSlot, activityType) {
  const ts = String(timeSlot || '').toLowerCase();
  if (ts === 'reperibilita_notte') return 'Rep Notte';
  if (ts === 'reperibilita_giorno' || ts === 'reperibilita') return 'Rep Giorno';
  const cat = String(category || '').trim().toLowerCase();
  const at = String(activityType || 'clinico').trim().toLowerCase() || 'clinico';
  if (cat === 'sala_operatoria' && at === 'non_clinico') return 'Amb Anest';
  if (cat === 'direzione' || at === 'direzione') return 'Direzione';
  if (cat === 'formazione' || at === 'formazione') return 'Form';
  const map = {
    terapia_intensiva: 'TI',
    sala_operatoria: 'SO1',
    nora: 'RMN',
    medicina_dolore: 'MedDol',
    cure_palliative: 'CP',
  };
  if (map[cat]) return map[cat];
  const tail = String(category || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!tail) return 'Slot';
  return tail.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Nome slot sempre non vuoto, come `backend/main.py` → `_ensure_non_empty_shift_slot_name`:
 * etichetta Piano = valore da persistere (creazione manuale, calendario, API client).
 */
export function ensurePlannerSlotName(category, rawName, timeSlot, activityType) {
  const raw = String(rawName ?? '').trim();
  const at = String(activityType ?? 'clinico').trim().toLowerCase() || 'clinico';
  if (!raw) {
    return defaultPlannerSlotName(category, timeSlot, activityType);
  }
  const c = canonicalizeSlotNameKeepIndex(category, raw, timeSlot, activityType);
  if (c) return c;
  return defaultPlannerSlotName(category, timeSlot, activityType);
}

/** Identificativo univoco settimana ISO 8601: "YYYY-Wnn" (es. 2025-W06) */
export function getISOWeekString(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - day);
  const year = d.getFullYear();
  const yearStart = new Date(year, 0, 1);
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${year}-W${String(weekNo).padStart(2, '0')}`;
}

/** Numero settimana ISO (1-53) per una data */
export function getISOWeekNumber(dateStr) {
  const ws = getISOWeekString(dateStr);
  return parseInt(ws.split('-W')[1], 10);
}

/** Anno di settimana ISO (come `getISOWeekString`; può differire dall'anno civile del lunedì stesso). */
export function getISOWeekYear(dateStr) {
  const s = String(dateStr || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date().getFullYear();
  }
  const ws = getISOWeekString(s);
  const prefix = String(ws || '').split('-W')[0];
  const y = parseInt(prefix, 10);
  return Number.isFinite(y) ? y : new Date(`${s}T12:00:00`).getFullYear();
}

/** Restituisce il lunedì (YYYY-MM-DD) della settimana ISO indicata (anno + numero 1-53) */
export function getMondayOfISOWeek(year, weekNum) {
  const firstMon = getMondayOfWeek(`${year}-01-04`);
  const d = new Date(firstMon + 'T12:00:00');
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return formatLocalISODate(d);
}

/** Restituisce la stringa intervallo date (lun–dom) per anno + settimana ISO */
export function getISOWeekDateRangeStr(year, weekNum) {
  const monday = getMondayOfISOWeek(year, weekNum);
  const d = new Date(monday + 'T12:00:00');
  d.setDate(d.getDate() + 6);
  const sunday = formatLocalISODate(d);
  return `${formatDateIt(monday)} – ${formatDateIt(sunday)}`;
}

export function getDaysInMonth(year, month) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const days = [];
  for (let d = 1; d <= last.getDate(); d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const date = new Date(year, month - 1, d);
    const wd = date.getDay();
    const weekday = wd === 0 ? 'dom' : WEEKDAYS[wd - 1];
    days.push({
      date: dateStr,
      day: d,
      weekday,
      weekdayShort: WEEKDAY_SHORT[weekday] || weekday,
      weekdayLabel: WEEKDAY_LABELS[weekday] || weekday,
      isWeekend: weekday === 'sab' || weekday === 'dom',
      isoWeek: getISOWeekString(dateStr),
    });
  }
  return days;
}

export function getMonthYearFromStr(str) {
  const [y, m] = str.split('-').map(Number);
  return { year: y, month: m };
}

/** Nomi mesi abbreviati per dropdown */
export const MONTH_NAMES = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

/** HTML per selettore mese con dropdown (mese + anno) - un click per scegliere
 * @param {object} [options]
 * @param {string[]} [options.monthLabels] etichette 12 mesi (default abbreviate)
 * @param {number[]} [options.years] elenco anni (default ±2 intorno all'anno corrente)
 */
/**
 * Barra delle ore sulla scala dell'intero bimestre, con due segnalini e un tratto chiaro:
 * - riempimento e pallino = dove sei **a oggi** (ore coperte fino a dove i turni sono stati prodotti);
 * - asta = dove dovresti essere alla stessa data;
 * - tratto chiaro dopo il pallino = ore già coperte **dopo** quella data (tipicamente ferie già
 *   registrate): sono copertura acquisita, ma non fanno parte del «dove sei», altrimenti il pallino
 *   confronterebbe il bimestre intero con il dovuto a oggi.
 * La distanza fra pallino e asta è lo scostamento dalla traiettoria. Usata in Carichi e in Home.
 */
export function hoursPaceBarHTML({ coperteAOggi, coperteFuture = 0, dovuteAOggi, dovuteTotali, label = '', large = false }) {
  const scala = Number(dovuteTotali) || 0;
  const clamp = (v) => Math.max(0, Math.min(100, v));
  const oggi = Number(coperteAOggi) || 0;
  const future = Math.max(0, Number(coperteFuture) || 0);
  const youLeft = scala > 0 ? clamp((oggi / scala) * 100) : 0;
  const futureEnd = scala > 0 ? clamp(((oggi + future) / scala) * 100) : youLeft;
  const goalLeft = scala > 0 ? clamp(((Number(dovuteAOggi) || 0) / scala) * 100) : 0;
  // In pari si giudica sulle ore a oggi contro il dovuto a oggi: le ferie future non contano.
  const atPace = oggi >= (Number(dovuteAOggi) || 0) - 0.05;
  const safeLabel = String(label ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const futureHtml = futureEnd - youLeft > 0.05
    ? `<div class="hours-bar-future" style="left: ${youLeft.toFixed(1)}%; width: ${(futureEnd - youLeft).toFixed(1)}%"></div>`
    : '';
  return `<div class="hours-bar${large ? ' hours-bar-lg' : ''}${atPace ? ' at-pace' : ''}" role="img" aria-label="${safeLabel}" title="${safeLabel}">
      <div class="hours-bar-fill" style="width: ${youLeft.toFixed(1)}%"></div>
      ${futureHtml}
      <i class="hours-bar-goal" style="left: ${goalLeft.toFixed(1)}%"></i>
      <i class="hours-bar-you" style="left: ${youLeft.toFixed(1)}%"></i>
    </div>`;
}

export function monthSelectorHTML(id, currentMonth, options = {}) {
  const { monthLabels = MONTH_NAMES, years: yearsOpt } = options;
  const { year, month } = getMonthYearFromStr(currentMonth);
  const currentYear = new Date().getFullYear();
  const years = yearsOpt ?? [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  return `
    <div class="month-selector" style="display: flex; gap: 0.5rem; align-items: flex-end;">
      <div>
        <label style="font-size: 0.8rem;">Mese</label>
        <select id="${id}-month" class="month-select" title="Seleziona mese">
          ${monthLabels.map((name, i) => `<option value="${i + 1}" ${i + 1 === month ? 'selected' : ''}>${name}</option>`).join('')}
        </select>
      </div>
      <div>
        <label style="font-size: 0.8rem;">Anno</label>
        <select id="${id}-year" class="year-select" title="Seleziona anno">
          ${years.map(y => `<option value="${y}" ${y === year ? 'selected' : ''}>${y}</option>`).join('')}
        </select>
      </div>
    </div>
  `;
}

/** Restituisce YYYY-MM dal selettore mese */
export function getMonthSelectorValue(container, id) {
  const monthEl = container.querySelector(`#${id}-month`);
  const yearEl = container.querySelector(`#${id}-year`);
  if (!monthEl || !yearEl) return null;
  const m = monthEl.value;
  const y = yearEl.value;
  return `${y}-${String(m).padStart(2, '0')}`;
}

/** Collega il selettore mese a un callback onChange(YYYY-MM) */
export function setupMonthSelector(container, id, onChange) {
  const monthEl = container.querySelector(`#${id}-month`);
  const yearEl = container.querySelector(`#${id}-year`);
  if (!monthEl || !yearEl) return;
  const fire = () => {
    const val = getMonthSelectorValue(container, id);
    if (val) onChange(val);
  };
  monthEl.addEventListener('change', fire);
  yearEl.addEventListener('change', fire);
}

/** Restituisce le 7 date (lun-dom) a partire dal lunedì della settimana */
export function getWeekDates(weekStart) {
  const d = new Date(weekStart + 'T12:00:00');
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(d);
    x.setDate(d.getDate() + i);
    dates.push(formatLocalISODate(x));
  }
  return dates;
}

/** Restituisce il lunedì (YYYY-MM-DD) della settimana contenente la data */
export function getMondayOfWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const wd = d.getDay() || 7;
  d.setDate(d.getDate() - wd + 1);
  return formatLocalISODate(d);
}

/** Aggiunge `deltaDays` a una data ISO YYYY-MM-DD (mezzogiorno locale). */
export function addCalendarDays(isoDate, deltaDays) {
  const s = (isoDate || '').slice(0, 10);
  if (!s) return '';
  const d = new Date(s + 'T12:00:00');
  d.setDate(d.getDate() + (deltaDays | 0));
  return formatLocalISODate(d);
}

export function getWeekStart(dateStr) {
  return getMondayOfWeek(dateStr);
}

/** Restituisce i sabati del mese (date YYYY-MM-DD) per identificare i weekend */
export function getWeekendsInMonth(year, month) {
  const days = getDaysInMonth(year, month);
  return days.filter(d => d.weekday === 'sab').map(d => d.date);
}

/** Restituisce i sabati nell'intervallo settimane (weekStartFrom, weekStartTo = lunedì) */
export function getWeekendsInWeekRange(weekStartFrom, weekStartTo) {
  const days = getDaysInWeekRange(weekStartFrom, weekStartTo);
  return days.filter(d => d.weekday === 'sab').map(d => d.date);
}

/** Restituisce la domenica (YYYY-MM-DD) della settimana che inizia il lunedì weekStart. */
export function getWeekEndDate(weekStart) {
  const d = new Date(weekStart + 'T12:00:00');
  d.setDate(d.getDate() + 6);
  return formatLocalISODate(d);
}

export function getWeekEnd(dateStr) {
  return getWeekEndDate(getMondayOfWeek(dateStr));
}

/** Primo e ultimo giorno (YYYY-MM-DD) del mese civile successivo rispetto a `referenceDate` (locale). */
export function getNextCalendarMonthBounds(referenceDate = new Date()) {
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const y = ref.getFullYear();
  const m = ref.getMonth();
  const firstNext = new Date(y, m + 1, 1);
  const ny = firstNext.getFullYear();
  const nm = firstNext.getMonth();
  const monthStart = formatLocalISODate(new Date(ny, nm, 1));
  const monthEnd = formatLocalISODate(new Date(ny, nm + 1, 0));
  return { monthStart, monthEnd };
}

/**
 * Span calendario lun–dom ISO che include `monthStart` e `monthEnd` (YYYY-MM-DD dello stesso mese civile).
 */
export function getIsoWeekGridSpanForCalendarMonth(monthStart, monthEnd) {
  const gridStart = getMondayOfWeek(monthStart);
  const gridEnd = getWeekEndDate(getMondayOfWeek(monthEnd));
  return { gridStart, gridEnd };
}

/** Tutti i giorni YYYY-MM-DD da `startIso` a `endIso` inclusivi (richiede startIso <= endIso). */
export function enumerateCalendarDaysInclusive(startIso, endIso) {
  const days = [];
  let d = (startIso || '').slice(0, 10);
  const end = (endIso || '').slice(0, 10);
  while (d && end && d <= end) {
    days.push(d);
    d = addCalendarDays(d, 1);
  }
  return days;
}

/** Giorni da lunedì weekStartFrom a domenica weekStartTo (entrambi YYYY-MM-DD, lunedì) */
export function getDaysInWeekRange(weekStartFrom, weekStartTo) {
  const days = [];
  const start = new Date(weekStartFrom + 'T12:00:00');
  const end = new Date(weekStartTo + 'T12:00:00');
  end.setDate(end.getDate() + 6);
  const current = new Date(start);
  while (current <= end) {
    const dateStr = formatLocalISODate(current);
    const wd = current.getDay();
    const weekday = wd === 0 ? 'dom' : WEEKDAYS[wd - 1];
    days.push({
      date: dateStr,
      day: current.getDate(),
      weekday,
      weekdayShort: WEEKDAY_SHORT[weekday] || weekday,
      weekdayLabel: WEEKDAY_LABELS[weekday] || weekday,
      isWeekend: weekday === 'sab' || weekday === 'dom',
      isoWeek: getISOWeekString(dateStr),
    });
    current.setDate(current.getDate() + 1);
  }
  return days;
}

/** Opzioni settimana per dropdown: tutte le settimane dell'anno ISO `isoYear` (non anno civile). */
function getWeekOptionsForYear(isoYear) {
  const options = [];
  let monday = getMondayOfISOWeek(isoYear, 1);
  for (let n = 0; n < 54; n++) {
    if (getISOWeekYear(monday) !== isoYear) break;
    options.push({
      value: monday,
      label: `${getISOWeekString(monday)} (${formatDateIt(monday)})`,
    });
    const d = new Date(monday + 'T12:00:00');
    d.setDate(d.getDate() + 7);
    monday = formatLocalISODate(d);
  }
  return options;
}

/** Anni disponibili per selettore (navigazione agile) */
const YEAR_RANGE_START = 2020;
const YEAR_RANGE_END = 2035;

/** HTML per selettore intervallo settimane con Anno. labels: { from: 'Da settimana', to: 'A settimana' } */
export function weekRangeSelectorHTML(id, weekFrom, weekTo, labels = {}) {
  const fromLabel = labels.from ?? 'Da settimana';
  const toLabel = labels.to ?? 'A settimana';
  const defaultFrom = weekFrom || getMondayOfWeek(formatLocalISODate(new Date()));
  const defaultTo = weekTo || defaultFrom;
  const fromYear = getISOWeekYear(defaultFrom);
  const toYear = getISOWeekYear(defaultTo);
  const years = [];
  for (let y = YEAR_RANGE_START; y <= YEAR_RANGE_END; y++) years.push(y);
  const weeksFromYear = getWeekOptionsForYear(fromYear);
  const weeksToYear = getWeekOptionsForYear(toYear);
  return `
    <div class="week-range-selector">
      <div class="week-range-bound">
        <label class="week-range-label">${fromLabel}</label>
        <div class="week-range-pair">
          <select id="${id}-year-from" class="week-range-year-select" title="Anno di settimana ISO (inizio)">
            ${years.map(y => `<option value="${y}" ${y === fromYear ? 'selected' : ''}>${y}</option>`).join('')}
          </select>
          <select id="${id}-from" class="week-range-week-select" title="Settimana di inizio (lunedì)">
            ${weeksFromYear.map(w => `<option value="${w.value}" ${w.value === defaultFrom ? 'selected' : ''}>${w.label}</option>`).join('')}
          </select>
        </div>
      </div>
      <div class="week-range-bound">
        <label class="week-range-label">${toLabel}</label>
        <div class="week-range-pair">
          <select id="${id}-year-to" class="week-range-year-select" title="Anno di settimana ISO (fine)">
            ${years.map(y => `<option value="${y}" ${y === toYear ? 'selected' : ''}>${y}</option>`).join('')}
          </select>
          <select id="${id}-to" class="week-range-week-select" title="Settimana di fine (lunedì)">
            ${weeksToYear.map(w => `<option value="${w.value}" ${w.value === defaultTo ? 'selected' : ''}>${w.label}</option>`).join('')}
          </select>
        </div>
      </div>
    </div>
  `;
}

/** Restituisce { weekFrom, weekTo } dal selettore */
export function getWeekRangeSelectorValue(container, id) {
  const fromEl = container.querySelector(`#${id}-from`);
  const toEl = container.querySelector(`#${id}-to`);
  if (!fromEl || !toEl) return null;
  return { weekFrom: fromEl.value, weekTo: toEl.value };
}

/** Aggiorna le opzioni settimana in base all'anno di settimana ISO scelto nella tendina anno. */
export function refreshWeekSelectOptions(selectEl, yearSelectEl) {
  const year = parseInt(yearSelectEl?.value, 10) || getISOWeekYear(formatLocalISODate(new Date()));
  const weeks = getWeekOptionsForYear(year);
  const currentVal = selectEl.value;
  selectEl.innerHTML = weeks.map(w => `<option value="${w.value}">${w.label}</option>`).join('');
  const stillValid = weeks.some(w => w.value === currentVal);
  selectEl.value = stillValid ? currentVal : (weeks[0]?.value || '');
}

/** Collega il selettore intervallo settimane a un callback.
 *  onChange(val): val = { weekFrom, weekTo }. Se weekFrom > weekTo, si corregge in base a quale campo è stato modificato.
 */
export function setupWeekRangeSelector(container, id, onChange) {
  const fromEl = container.querySelector(`#${id}-from`);
  const toEl = container.querySelector(`#${id}-to`);
  const yearFromEl = container.querySelector(`#${id}-year-from`);
  const yearToEl = container.querySelector(`#${id}-year-to`);
  if (!fromEl || !toEl) return;

  const fire = (source) => {
    const val = getWeekRangeSelectorValue(container, id);
    if (!val) return;
    let { weekFrom, weekTo } = val;
    if (weekFrom && weekTo && weekFrom > weekTo) {
      if (source === 'from') {
        weekTo = weekFrom;
        toEl.value = weekFrom;
        if (yearToEl) {
          const toYear = getISOWeekYear(weekFrom);
          if (parseInt(yearToEl.value, 10) !== toYear) {
            yearToEl.value = toYear;
            refreshWeekSelectOptions(toEl, yearToEl);
            toEl.value = weekFrom;
          }
        }
      } else {
        weekFrom = weekTo;
        fromEl.value = weekTo;
        if (yearFromEl) {
          const fromYear = getISOWeekYear(weekTo);
          if (parseInt(yearFromEl.value, 10) !== fromYear) {
            yearFromEl.value = fromYear;
            refreshWeekSelectOptions(fromEl, yearFromEl);
            fromEl.value = weekTo;
          }
        }
      }
    }
    onChange({ weekFrom, weekTo });
  };

  if (yearFromEl) {
    yearFromEl.addEventListener('change', () => {
      refreshWeekSelectOptions(fromEl, yearFromEl);
      fire('from');
    });
  }
  if (yearToEl) {
    yearToEl.addEventListener('change', () => {
      refreshWeekSelectOptions(toEl, yearToEl);
      fire('to');
    });
  }
  fromEl.addEventListener('change', () => fire('from'));
  toEl.addEventListener('change', () => fire('to'));
}

/** Abbreviazione categoria slot per report */
const CAT_SHORT = {
  terapia_intensiva: 'TI',
  sala_operatoria: 'SO',
  nora: 'NORA',
  medicina_dolore: 'MD',
  cure_palliative: 'CP',
  formazione: 'FOR',
  direzione: 'Dir',
};

/** Estrae data e fascia da slotId (es. 2026-03-05-pomeriggio-sala_operatoria-...) */
function parseSlotIdForDisplay(slotId) {
  const parts = String(slotId).split('-');
  if (parts.length >= 5) {
    const dateStr = `${parts[0]}-${parts[1]}-${parts[2]}`;
    const timeSlot = parts[3] || '';
    const cat = parts[4] || '';
    const catShort = CAT_SHORT[cat] || cat;
    const timeLabel = timeSlot === 'mattino' ? 'M' : timeSlot === 'pomeriggio' ? 'P' : timeSlot === 'prolungato' ? 'Pr' : timeSlot === 'notte' ? 'N' : timeSlot;
    return `${formatDateIt(dateStr)} ${timeLabel} ${catShort}`.trim();
  }
  return slotId;
}

/**
 * Trasforma i diagnostics grezzi della generazione turni in un report leggibile.
 * @param {string[]} diagnostics - array di messaggi da shiftGenerator
 * @param {number} overtimeCount - numero slot non assegnati (oltre monte orario)
 * @returns {string} report formattato
 */
export function formatLevelingReport(diagnostics, overtimeCount = 0) {
  if (!diagnostics || diagnostics.length === 0) {
    return overtimeCount > 0
      ? `⚠ ${overtimeCount} slot non assegnati (oltre monte orario). Assegnazione manuale consigliata.`
      : '✓ Generazione completata senza problemi.';
  }

  const weeksWithGap = [];
  const weeksWithUnassigned = [];
  const relaxedSlots = [];
  const failedSlots = [];
  let repInfo = null;
  let infrasettInfo = null;
  let competenzaInfo = null;
  const cpMetrics = {
    coverage: null,
    slack: null,
    debt: null,
    affinity: null,
    dispersion: null,
    aggregateNotti: null,
    aggregateRep: null,
  };
  const otherLines = [];

  for (const line of diagnostics) {
    const trimmed = String(line || '').trim();
    if (!trimmed) continue;

    if (trimmed === 'CP-SAT lessicografico completato.' || trimmed === 'Ottimizzazione completata con CP-SAT (approccio lessicografico).') continue;

    const coverageMatch = trimmed.match(/^Coverage\(unassigned\)=(\d+)$/);
    if (coverageMatch) {
      cpMetrics.coverage = parseInt(coverageMatch[1], 10);
      continue;
    }
    const coverageReadableMatch = trimmed.match(/^Slot rimasti scoperti: (\d+)$/);
    if (coverageReadableMatch) {
      cpMetrics.coverage = parseInt(coverageReadableMatch[1], 10);
      continue;
    }
    const slackMatch = trimmed.match(/^Slack\(max6\)=(\d+)$/);
    if (slackMatch) {
      cpMetrics.slack = parseInt(slackMatch[1], 10);
      continue;
    }
    const slackReadableMatch = trimmed.match(/^Deroghe al limite max 6 turni\/settimana: (\d+)$/);
    if (slackReadableMatch) {
      cpMetrics.slack = parseInt(slackReadableMatch[1], 10);
      continue;
    }
    const debtMatch = trimmed.match(/^DebtDeviation=(\d+)$/);
    if (debtMatch) {
      cpMetrics.debt = parseInt(debtMatch[1], 10);
      continue;
    }
    const debtReadableMatch = trimmed.match(/^Scostamento dai target di carico: (\d+)$/);
    if (debtReadableMatch) {
      cpMetrics.debt = parseInt(debtReadableMatch[1], 10);
      continue;
    }
    const affinityReadableMatch = trimmed.match(/^Scostamento affinita' branche M\/P: (\d+)$/);
    if (affinityReadableMatch) {
      cpMetrics.affinity = parseInt(affinityReadableMatch[1], 10);
      continue;
    }
    const dispersionMatch = trimmed.match(/^Dispersion=(\d+)$/);
    if (dispersionMatch) {
      cpMetrics.dispersion = parseInt(dispersionMatch[1], 10);
      continue;
    }
    const dispersionReadableMatch = trimmed.match(/^Dispersione del carico tra medici: (\d+)$/);
    if (dispersionReadableMatch) {
      cpMetrics.dispersion = parseInt(dispersionReadableMatch[1], 10);
      continue;
    }
    const aggregateNottiMatch = trimmed.match(/^Aggregate notti fuori range: total=(\d+), sum\(raw-1\)=(\d+), sum\(raw\+1\)=(\d+)$/);
    if (aggregateNottiMatch) {
      cpMetrics.aggregateNotti = {
        total: parseInt(aggregateNottiMatch[1], 10),
        minus: parseInt(aggregateNottiMatch[2], 10),
        plus: parseInt(aggregateNottiMatch[3], 10),
      };
      continue;
    }
    const aggregateRepMatch = trimmed.match(/^Aggregate reperibilità fuori range: total=(\d+), sum\(raw-1\)=(\d+), sum\(raw\+1\)=(\d+)$/);
    if (aggregateRepMatch) {
      cpMetrics.aggregateRep = {
        total: parseInt(aggregateRepMatch[1], 10),
        minus: parseInt(aggregateRepMatch[2], 10),
        plus: parseInt(aggregateRepMatch[3], 10),
      };
      continue;
    }
    const aggregateNottiReadableMatch = trimmed.match(/^Notti fuori intervallo teorico: slot totali=(\d+), min compatibile=(\d+), max compatibile=(\d+)$/);
    if (aggregateNottiReadableMatch) {
      cpMetrics.aggregateNotti = {
        total: parseInt(aggregateNottiReadableMatch[1], 10),
        minus: parseInt(aggregateNottiReadableMatch[2], 10),
        plus: parseInt(aggregateNottiReadableMatch[3], 10),
      };
      continue;
    }
    const aggregateRepReadableMatch = trimmed.match(/^Reperibilita' fuori intervallo teorico: slot totali=(\d+), min compatibile=(\d+), max compatibile=(\d+)$/);
    if (aggregateRepReadableMatch) {
      cpMetrics.aggregateRep = {
        total: parseInt(aggregateRepReadableMatch[1], 10),
        minus: parseInt(aggregateRepReadableMatch[2], 10),
        plus: parseInt(aggregateRepReadableMatch[3], 10),
      };
      continue;
    }
    const ferialiMatch = line.match(/Feriali sett\. (\d{4}-\d{2}-\d{2}): (\d+) round\.(?: WARN )?[Gg]ap (\d+(?:\.\d+)?)h\.?(?: (\d+) non assegnati\.)?/);
    if (ferialiMatch) {
      const [, ws, , gapStr, unassigned] = ferialiMatch;
      const gap = parseFloat(gapStr);
      weeksWithGap.push({ ws, gap, unassigned: unassigned ? parseInt(unassigned, 10) : 0 });
      continue;
    }
    const validMatch = line.match(/Validazione: sett\. (\d{4}-\d{2}-\d{2}) gap (\d+(?:\.\d+)?)h > 6h\./);
    if (validMatch) continue; // già catturato da Feriali

    const slotMatch = line.match(/Slot ([^:]+): usato livello (\w+) \((\d+) cand\.\)/);
    if (slotMatch) {
      relaxedSlots.push({ slotId: slotMatch[1], level: slotMatch[2], candidates: slotMatch[3] });
      continue;
    }
    const failMatch = line.match(/FALLITO Slot ([^:]+): (.+)/);
    if (failMatch) {
      failedSlots.push({ slotId: failMatch[1], reason: failMatch[2] });
      continue;
    }
    const repMatch = line.match(/Reperibilità: (\d+) passate\. Gap 4w: (\d+(?:\.\d+)?)h\./);
    if (repMatch) {
      repInfo = { passes: repMatch[1], gap: repMatch[2] };
      continue;
    }
    const infrasettMatch = line.match(/Livellamento infrasettimanali: (\d+) round\. Gap ore: (\d+(?:\.\d+)?)h, gap giorni: (\d+)\./);
    if (infrasettMatch) {
      infrasettInfo = { rounds: infrasettMatch[1], gapH: infrasettMatch[2], gapDays: infrasettMatch[3] };
      continue;
    }
    const compMatch = line.match(/Competenza: (\d+) round\./);
    if (compMatch) { competenzaInfo = compMatch[1]; continue; }
    if (line.includes('Competenza cross-day') || line.includes('Bilanciamento M/P')) continue; // info secondaria
    if (line.includes('Weekend, Notti e Reperibilità')) continue; // intro
    otherLines.push(line);
  }

  const lines = [];
  lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  lines.push('  REPORT GENERAZIONE TURNI');
  lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  lines.push('');

  const hasIssues = weeksWithGap.some(w => w.gap > 6)
    || overtimeCount > 0
    || failedSlots.length > 0
    || (cpMetrics.coverage ?? 0) > 0
    || (cpMetrics.slack ?? 0) > 0;
  lines.push(hasIssues ? '⚠ Completato con criticita da verificare' : '✓ Completato senza criticita');
  lines.push('');

  if (
    cpMetrics.coverage !== null
    || cpMetrics.slack !== null
    || cpMetrics.debt !== null
    || cpMetrics.affinity !== null
    || cpMetrics.dispersion !== null
    || cpMetrics.aggregateNotti
    || cpMetrics.aggregateRep
  ) {
    lines.push('Riepilogo ottimizzazione:');
    if (cpMetrics.coverage !== null) {
      lines.push(`  • Slot rimasti scoperti: ${cpMetrics.coverage}`);
    }
    if (cpMetrics.slack !== null) {
      lines.push(`  • Deroghe al limite "max 6 turni/settimana": ${cpMetrics.slack}`);
    }
    if (cpMetrics.debt !== null) {
      lines.push(`  • Scostamento dai target di carico: ${cpMetrics.debt} (piu' basso e' meglio)`);
    }
    if (cpMetrics.affinity !== null) {
      lines.push(`  • Scostamento affinita' branche M/P: ${cpMetrics.affinity} (piu' basso e' meglio)`);
    }
    if (cpMetrics.dispersion !== null) {
      lines.push(`  • Dispersione carico tra medici: ${cpMetrics.dispersion} (piu' basso e' meglio)`);
    }
    if (cpMetrics.aggregateNotti) {
      lines.push(`  • Notti fuori intervallo: ${cpMetrics.aggregateNotti.total} medici (scostamento cumulato sotto=${cpMetrics.aggregateNotti.minus}, sopra=${cpMetrics.aggregateNotti.plus})`);
    }
    if (cpMetrics.aggregateRep) {
      lines.push(`  • Reperibilita' fuori intervallo: ${cpMetrics.aggregateRep.total} medici (scostamento cumulato sotto=${cpMetrics.aggregateRep.minus}, sopra=${cpMetrics.aggregateRep.plus})`);
    }
    lines.push('');
  }

  if (overtimeCount > 0) {
    lines.push(`• ${overtimeCount} slot non assegnati (oltre monte orario) → assegnazione manuale`);
    lines.push('');
  }

  if (weeksWithGap.length > 0) {
    const problematic = weeksWithGap.filter(w => w.gap > 6 || w.unassigned > 0);
    if (problematic.length > 0) {
      lines.push('Settimane da verificare (gap ore o slot scoperti):');
      for (const w of problematic) {
        const [y, m, d] = w.ws.split('-');
        const label = `${d}/${m}/${y}`;
        const parts = [`gap ${w.gap}h`];
        if (w.unassigned > 0) parts.push(`${w.unassigned} slot scoperti`);
        lines.push(`  • ${label}: ${parts.join(' — ')}`);
      }
      lines.push('');
    }
  }

  if (failedSlots.length > 0) {
    lines.push('Slot non assegnabili:');
    for (const s of failedSlots) {
      lines.push(`  ✗ ${parseSlotIdForDisplay(s.slotId)}: ${s.reason}`);
    }
    lines.push('');
  }

  if (relaxedSlots.length > 0) {
    lines.push('Slot con assegnazione "rilassata" (verificare se ok):');
    const byDate = {};
    for (const s of relaxedSlots) {
      const key = parseSlotIdForDisplay(s.slotId);
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(s.level);
    }
    for (const [label, levels] of Object.entries(byDate)) {
      const uniq = [...new Set(levels)];
      lines.push(`  • ${label} (${uniq.join(', ')})`);
    }
    lines.push('');
  }

  if (repInfo && (parseFloat(repInfo.gap) > 0 || parseInt(repInfo.passes, 10) > 1)) {
    lines.push(`Reperibilita': ${repInfo.passes} passate, gap su 4 settimane = ${repInfo.gap}h`);
    lines.push('');
  }
  if (infrasettInfo && (parseFloat(infrasettInfo.gapH) > 0 || parseInt(infrasettInfo.gapDays, 10) > 0)) {
    lines.push(`Infrasettimanali: gap ore = ${infrasettInfo.gapH}h, gap giorni = ${infrasettInfo.gapDays}`);
    lines.push('');
  }

  if (otherLines.length > 0) {
    lines.push('Altre informazioni:');
    for (const l of otherLines) lines.push(`  • ${l}`);
  }

  return lines.join('\n');
}

/** Formatta data in GG-MM-AAAA (es. 15-02-2025) */
export function formatDateIt(dateStr) {
  if (!dateStr || dateStr.length < 10) return dateStr || '';
  const [y, m, d] = dateStr.slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
}

export function formatDateShort(dateStr) {
  return formatDateIt(dateStr);
}

export function getWeekdayFromDate(dateStr) {
  const d = new Date(dateStr);
  const wd = d.getDay();
  return wd === 0 ? 'dom' : WEEKDAYS[wd - 1];
}

export function formatDateWithWeekday(dateStr) {
  const wd = getWeekdayFromDate(dateStr);
  const short = WEEKDAY_SHORT[wd] || wd;
  return `${formatDateIt(dateStr)} ${short}`;
}

/** Festivi Repubblica di San Marino (calendario ufficiale uffici). Ritorna Set di date YYYY-MM-DD per l'anno */
export function getFestiviInfrasettimanali(year) {
  const festiviFissi = [
    [1, 1],   // Capodanno
    [6, 1],   // Epifania
    [5, 2],   // Sant'Agata e Liberazione Alberoniana
    [25, 3],  // Anniversario dell'Arengo
    [1, 4],   // Investitura Capitani Reggenti
    [1, 5],   // Festa dei Lavoratori
    [28, 7],  // Caduta del Fascismo e Festa della Libertà
    [15, 8],  // Assunzione B.V. Maria
    [3, 9],   // San Marino - Patrono e Fondazione Repubblica
    [1, 10],  // Investitura Capitani Reggenti
    [1, 11],  // Tutti i Santi
    [2, 11],  // Commemorazione dei Defunti
    [8, 12],  // Immacolata Concezione
    [25, 12], // Natale
    [26, 12], // Santo Stefano
  ];
  const set = new Set();
  for (const [day, month] of festiviFissi) {
    const d = new Date(year, month - 1, day);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) {
      set.add(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    }
  }
  const easter = getEaster(year);
  const lunediAngelo = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + 1);
  if (lunediAngelo.getDay() !== 0 && lunediAngelo.getDay() !== 6) {
    set.add(`${lunediAngelo.getFullYear()}-${String(lunediAngelo.getMonth() + 1).padStart(2, '0')}-${String(lunediAngelo.getDate()).padStart(2, '0')}`);
  }
  const corpusDomini = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + 60);
  if (corpusDomini.getDay() !== 0 && corpusDomini.getDay() !== 6) {
    set.add(`${corpusDomini.getFullYear()}-${String(corpusDomini.getMonth() + 1).padStart(2, '0')}-${String(corpusDomini.getDate()).padStart(2, '0')}`);
  }
  return set;
}

function getEaster(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/**
 * Rimuove la selezione testo nativa del browser.
 * Su iOS Safari un long-press + drag sulla griglia assenze può lasciare testo selezionato sopra i pulsanti delle modali.
 */
export function clearNativeTextSelection() {
  try {
    const s = typeof window !== 'undefined' && window.getSelection && window.getSelection();
    if (s && s.rangeCount) s.removeAllRanges();
  } catch (_) {}
}

/**
 * Conferma in overlay (evita loop/blocchi di window.confirm su alcuni browser, es. Firefox su Windows).
 * @returns {Promise<boolean>}
 */
export function confirmInModal(message, options = {}) {
  const title = options.title ?? 'Conferma';
  const okLabel = options.okLabel ?? 'Continua';
  const cancelLabel = options.cancelLabel ?? 'Annulla';
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.style.maxWidth = 'min(420px, calc(100vw - 2rem))';

    const header = document.createElement('div');
    header.className = 'modal-header';
    const h3 = document.createElement('h3');
    h3.textContent = title;
    header.appendChild(h3);

    const body = document.createElement('div');
    body.className = 'modal-body';
    const p = document.createElement('p');
    p.style.margin = '0';
    p.style.whiteSpace = 'pre-wrap';
    p.textContent = message;
    body.appendChild(p);

    const footer = document.createElement('div');
    footer.className = 'modal-footer';
    const btnCancel = document.createElement('button');
    btnCancel.type = 'button';
    btnCancel.className = 'btn btn-secondary';
    btnCancel.textContent = cancelLabel;
    const btnOk = document.createElement('button');
    btnOk.type = 'button';
    btnOk.className = 'btn btn-primary';
    btnOk.textContent = okLabel;
    footer.appendChild(btnCancel);
    footer.appendChild(btnOk);

    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlay.appendChild(modal);

    let settled = false;
    function done(value) {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKey, true);
      overlay.remove();
      resolve(value);
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        done(false);
      }
    }

    btnCancel.addEventListener('click', () => done(false));
    btnOk.addEventListener('click', () => done(true));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) done(false);
    });
    document.addEventListener('keydown', onKey, true);

    clearNativeTextSelection();
    document.body.appendChild(overlay);
    btnOk.focus();
  });
}
